import type { ComponentProps } from "react";
import { HtMedia, HtVideo } from "@hightouch/htui";
import type { Thumbnail } from "src/pages/ad-studio/insights/performance/view/types";

type MediaPreviewProps = {
  preview: Thumbnail;
  boxSize: ComponentProps<typeof HtMedia>["boxSize"];
  /** `"lazy"` waits until the preview nears the viewport, so a long list does not open a player per video at once. */
  loading?: ComponentProps<typeof HtMedia>["loading"];
  /** `"scrub"` seeks through a video as the pointer moves across it. */
  videoPlayback?: "none" | "scrub";
};

/** A small preview of an ad's creative; videos hold on their first frame unless they scrub. */
export const MediaPreview = ({
  preview,
  boxSize,
  loading,
  videoPlayback = "none",
}: MediaPreviewProps) => {
  switch (preview.kind) {
    case "video": {
      return (
        <HtVideo
          decorative
          src={preview.url}
          playback={videoPlayback}
          loading={loading}
          boxSize={boxSize}
          borderRadius="sm"
          objectFit="cover"
          bg="base.border"
          flexShrink={0}
        />
      );
    }
    case "image": {
      return (
        <HtMedia
          decorative
          src={preview.url}
          loading={loading}
          boxSize={boxSize}
          borderRadius="sm"
          objectFit="cover"
          bg="base.border"
          flexShrink={0}
        />
      );
    }
    default: {
      return preview.kind satisfies never;
    }
  }
};
